import * as React from 'react'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'

export interface ImageMetadata {
	src: string
	alt?: string
	title?: string
	width?: string
	height?: string
	className?: string
}

interface ImageMetadataDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	initialData: ImageMetadata
	onSave: (data: ImageMetadata) => void
}

export function ImageMetadataDialog({
	open,
	onOpenChange,
	initialData,
	onSave,
}: ImageMetadataDialogProps) {
	// Use a key to force component remount when initialData changes
	// This is cleaner than useEffect for this use case
	const dialogKey = React.useMemo(
		() => JSON.stringify(initialData),
		[initialData],
	)

	// Simple state management - initialized once per mount
	const [formData, setFormData] = React.useState<ImageMetadata>(initialData)

	const handleSave = () => {
		// Simple validation - just ensure src is present
		if (!formData.src.trim()) {
			console.error('Image source is required')
			return
		}

		// Clean up empty strings and apply semantic defaults
		const cleanedData: ImageMetadata = {
			src: formData.src,
			alt: formData.alt?.trim() || undefined,
			title: formData.title?.trim() || undefined,
			// Use semantic defaults for responsive images
			width: formData.width?.trim() || '100%',
			height: formData.height?.trim() || 'auto',
			className: formData.className?.trim() || undefined,
		}

		onSave(cleanedData)
		onOpenChange(false)
	}

	const updateField = (field: keyof ImageMetadata, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }))
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md" key={dialogKey}>
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Icon name="image" size="sm" />
						Edit Image Properties
					</DialogTitle>
				</DialogHeader>

				<div className="space-y-4">
					{/* Image Preview */}
					<div className="flex justify-center">
						<img
							src={formData.src}
							alt={formData.alt || 'Image preview'}
							className="max-h-32 max-w-full rounded border object-contain"
						/>
					</div>

					{/* Image Source (read-only) */}
					<div className="space-y-2">
						<Label htmlFor="image-src" className="text-sm font-medium">
							Image URL
						</Label>
						<Input
							id="image-src"
							value={formData.src}
							readOnly
							className="bg-muted"
						/>
					</div>

					{/* Alt Text */}
					<div className="space-y-2">
						<Label htmlFor="image-alt" className="text-sm font-medium">
							Alt Text
						</Label>
						<Textarea
							id="image-alt"
							value={formData.alt || ''}
							onChange={(e) => updateField('alt', e.target.value)}
							placeholder="Describe the image for accessibility and SEO..."
							rows={3}
						/>
					</div>

					{/* Title */}
					<div className="space-y-2">
						<Label htmlFor="image-title" className="text-sm font-medium">
							Title (Tooltip)
						</Label>
						<Input
							id="image-title"
							value={formData.title || ''}
							onChange={(e) => updateField('title', e.target.value)}
							placeholder="Optional tooltip text..."
						/>
					</div>

					{/* Dimensions */}
					<div className="space-y-3">
						<div className="grid grid-cols-2 gap-4">
							<div className="space-y-2">
								<Label htmlFor="image-width" className="text-sm font-medium">
									Width
								</Label>
								<Input
									id="image-width"
									value={formData.width || ''}
									onChange={(e) => updateField('width', e.target.value)}
									placeholder="100% (default)"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="image-height" className="text-sm font-medium">
									Height
								</Label>
								<Input
									id="image-height"
									value={formData.height || ''}
									onChange={(e) => updateField('height', e.target.value)}
									placeholder="auto (default)"
								/>
							</div>
						</div>
					</div>

					{/* Custom Classes */}
					<div className="space-y-2">
						<Label htmlFor="image-classes" className="text-sm font-medium">
							Custom Classes
						</Label>
						<Input
							id="image-classes"
							value={formData.className || ''}
							onChange={(e) => updateField('className', e.target.value)}
						/>
					</div>

					{/* Actions */}
					<div className="flex justify-end gap-2 pt-4">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</Button>
						<Button type="button" onClick={handleSave}>
							<Icon name="check" size="sm" />
							Save Changes
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	)
}
